import {useMutation, useQueryClient} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {message} from "antd";
import {createProductServiceRepository} from "../data/repositories";
import {ProductService} from "../domain/product-service.entity";
import {
  BookingQuestion,
  CreateProductServiceInput,
} from "../domain/product-service.types";
import {AnalyticsEvent} from "../../../utils/analytics";
import {GET_STORE_SERVICES_RQ_KEY} from "../../../react-query-hooks/store/services/constants";
import {
  CreateStoreServiceApiPayload,
  CreateStoreServiceApiResponse,
} from "../../../react-query-hooks/store/services/create-services/types";
import {CreateSessionApiBookingQuestionsPayload} from "../../../react-query-hooks/session/types";

/**
 * Adapts the legacy v1-shaped create payload (what the wizard produces) into
 * the canonical domain create input. Exported so the update hook can reuse it.
 */
export const toCreateInput = (
  payload: CreateStoreServiceApiPayload
): CreateProductServiceInput => ({
  name: payload.name,
  description: payload.description,
  deliveryTimeframeUnit: payload.delivery_timeframe_unit,
  deliveryTimeframeValue: payload.delivery_timeframe_value,
  urlRedirect: payload.url_redirect ?? null,
  redirectType: payload.redirect_type ?? "none",
  telegramGroupChatId: payload.telegram_group_chat_id ?? null,
  hideFromProfile: payload.hideFromProfile,
  requestPhoneNumber: payload.request_phone_number,
  // Absent on the payload means no cap; the key stays off the request entirely.
  ...(payload.purchaseLimit === undefined ? {} : {purchaseLimit: payload.purchaseLimit}),
  currencies: payload.currencies,
  isPayWhatYouWant: payload.isPayWhatYouWant,
  bookingQuestions: payload.bookingQuestions.map((q): BookingQuestion => {
    // Payload booking questions are snake_case `answer_type` (session union type).
    const item = q as {
      question: string;
      required: boolean;
      answer_type: string;
      options?: string[];
    };
    return {
      question: item.question,
      answerType: item.answer_type as BookingQuestion["answerType"],
      required: item.required,
      ...(item.options ? {options: item.options} : {}),
    };
  }),
  enableCurrencyConversion: payload.enable_currency_conversion,
  selectedCurrencies: payload.selected_currencies,
});

/**
 * Maps the created entity back to the legacy create response shape the wizard
 * reads (`data.productService.id`, snake_case fields).
 */
const toLegacyCreateResponse = (
  entity: ProductService
): CreateStoreServiceApiResponse => {
  const props = entity.toProps();

  return {
    productService: {
      id: props.id,
      userId: props.userId,
      name: props.name,
      slug: props.slug,
      tiny_url: props.tinyUrl,
      description: props.description,
      delivery_timeframe_unit: props.deliveryTimeframeUnit,
      delivery_timeframe_value: props.deliveryTimeframeValue,
      hideFromProfile: props.hideFromProfile,
      url_redirect: props.urlRedirect ?? "",
      statusId: props.statusId,
      request_phone_number: props.requestPhoneNumber,
      booking_questions:
        props.bookingQuestions.length > 0
          ? (props.bookingQuestions.map((q) => ({
              question: q.question,
              required: q.required,
              answer_type: q.answerType,
              ...(q.options ? {options: q.options} : {}),
            })) as CreateSessionApiBookingQuestionsPayload[])
          : null,
      createdAt: props.createdAt,
      updatedAt: props.updatedAt,
      deleted_at: null,
    },
  };
};

/**
 * Legacy-compatible create hook. Preserves the exact payload/response shape,
 * the list-cache invalidation, and the analytics events of the original hook,
 * while routing through the feature repository (always v2).
 *
 * @deprecated Use useCreateProductService from features/product-services instead.
 */
export const useCreateStoreServiceApi = () => {
  const queryClient = useQueryClient();
  const repository = createProductServiceRepository();

  return useMutation<
    {data: CreateStoreServiceApiResponse},
    AxiosError<{message: string}>,
    CreateStoreServiceApiPayload
  >({
    mutationFn: async (payload) => {
      const entity = await repository.create(toCreateInput(payload));
      return {data: toLegacyCreateResponse(entity)};
    },
    onError: (error: AxiosError<{message: string}>) => {
      if (error.message) {
        message.error(error.message);
      }
      AnalyticsEvent("Digital Products", "Services create fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Digital Products", "Services create success");
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: [GET_STORE_SERVICES_RQ_KEY, {page: 1}],
      }),
  });
};
